import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, Stat } from "../components/common";
import { itDate } from "../lib/constants";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { MessageCircle, Send, Copy, AlertTriangle, CheckCircle2, Users, User, RefreshCw } from "lucide-react";

const STATUS_LABEL = {
  prepared: "Pronto (anteprima)", accepted: "Accettato da Meta", sent: "Inviato",
  delivered: "Consegnato", read: "Letto", failed: "Fallito", annullato: "Annullato",
};
const STATUS_COLOR = {
  prepared: "#64748B", accepted: "#0369A1", sent: "#0369A1",
  delivered: "#15803D", read: "#15803D", failed: "#B91C1C", annullato: "#854D0E",
};

export default function WhatsAppCenter() {
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);

  const load = async () => {
    const [s, m] = await Promise.all([api.get("/whatsapp/status"), api.get("/whatsapp/messages")]);
    setStatus(s.data); setMessages(m.data);
  };
  useEffect(() => { load(); }, []);

  const runTeams = async () => { const { data } = await api.post("/whatsapp/run-teams"); toast.success(`Programma squadre generato (${data.sent})`); load(); };
  const runClients = async () => { const { data } = await api.post("/whatsapp/run-clients"); toast.success(`Promemoria clienti generati (${data.sent})`); load(); };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Testo copiato"); };

  if (!status) return <p className="text-muted-foreground animate-pulse">Caricamento…</p>;

  return (
    <div>
      <PageHeader title="Centro WhatsApp" subtitle="Programma squadre e promemoria clienti">
        <Button variant="outline" onClick={load}><RefreshCw size={15} className="mr-1" /> Aggiorna</Button>
      </PageHeader>

      {/* Configuration status */}
      <div className={`card-soft p-4 mb-5 border-l-4 ${status.meta_configured ? "border-emerald-500 bg-emerald-50" : "border-amber-500 bg-amber-50"}`} data-testid="wa-config-status">
        <div className="flex items-start gap-3">
          {status.meta_configured ? <CheckCircle2 className="text-emerald-600 shrink-0" /> : <AlertTriangle className="text-amber-600 shrink-0" />}
          <div>
            <p className="font-semibold">{status.meta_configured ? "Invio automatico ATTIVO (WhatsApp Cloud API configurato)" : "Invio automatico NON ancora attivo"}</p>
            {!status.meta_configured && (
              <div className="text-sm text-muted-foreground mt-1">
                <p>I messaggi vengono generati e programmati, ma per l'invio reale servono le credenziali Meta. Mancano:</p>
                <ul className="list-disc ml-5 mt-1">{status.missing.map((m) => <li key={m}>{m}</li>)}</ul>
                <p className="mt-2">Nel frattempo puoi usare <b>Prepara WhatsApp</b> (anteprima + copia + wa.me) qui sotto e nei singoli appuntamenti.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="card-soft p-5 lift">
          <h3 className="font-head font-bold flex items-center gap-2"><Users size={18} className="text-brand" /> Programma mattutino squadre</h3>
          <p className="text-sm text-muted-foreground mt-1">Genera il riepilogo dei montaggi di oggi per ogni squadra (dal calendario). Invio automatico ogni mattina alle {status.config.team_send_time} (Europe/Rome).</p>
          <Button className="bg-brand text-white mt-3 w-full" data-testid="btn-run-teams" onClick={runTeams}><Send size={15} className="mr-1" /> Genera programma squadre ora</Button>
        </div>
        <div className="card-soft p-5 lift">
          <h3 className="font-head font-bold flex items-center gap-2"><User size={18} className="text-brand" /> Promemoria clienti</h3>
          <p className="text-sm text-muted-foreground mt-1">Promemoria del montaggio confermato, anticipo {status.config.client_reminder_hours}h. Solo appuntamenti confermati e futuri.</p>
          <Button className="bg-brand text-white mt-3 w-full" data-testid="btn-run-clients" onClick={runClients}><Send size={15} className="mr-1" /> Genera promemoria clienti ora</Button>
        </div>
      </div>

      <h3 className="font-head font-bold mb-3">Registro messaggi ({messages.length})</h3>
      {messages.length === 0 ? <p className="text-sm text-muted-foreground">Nessun messaggio ancora generato.</p> : (
        <div className="space-y-3">
          {messages.map((m) => (
            <div key={m.id} data-testid={`wa-msg-${m.id}`} className="card-soft p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded-full capitalize bg-accent">{m.recipient_type}</span>
                  <span className="font-medium text-sm">{m.recipient_name}</span>
                  {m.recipient_phone && <span className="text-xs text-muted-foreground">{m.recipient_phone}</span>}
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ background: STATUS_COLOR[m.status] || "#64748B" }}>{STATUS_LABEL[m.status] || m.status}</span>
              </div>
              <pre className="text-xs bg-accent rounded-lg p-3 whitespace-pre-wrap font-sans max-h-48 overflow-y-auto">{m.body}</pre>
              {m.error && <p className="text-xs text-destructive mt-1">Errore: {m.error}</p>}
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={() => copy(m.body)}><Copy size={13} className="mr-1" /> Copia</Button>
                {m.wa_link && <a href={m.wa_link} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><MessageCircle size={13} className="mr-1" /> Prepara WhatsApp</Button></a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
